import { useEffect, useRef, useState, useCallback } from 'react';
import Phaser from 'phaser';
import { applyPixelFiltering } from '../../game/loaders/pixelFilter.js';
import {
  useGameStore, useJwt, useAuthHeaders, EventBus, findFurniture, nextOrientation, orientations, wallCatalog,
  wallPreviewFrames, WALL_ATLAS, DOOR_PALETTE, findDoor, FLOOR_ATLAS, floorMaterials, floorTerrains,
  LABEL_SIZES, LABEL_COLORS, MAX_LABEL_LENGTH,
  DEFAULT_DESK_SIZE, MIN_DESK_SIZE, MAX_DESK_SIZE, deskSizeOf, generateDeskSlots, SESSION_TYPES,
} from '@campus/core';
import type {
  ZoneConfig, ZoneAssetUrls, WallCatalogEntry, FurnitureCatalogEntry, MapLabel, LabelSize, LabelColor,
} from '@campus/core';
import { EditorScene } from '../../game/scenes/EditorScene.js';
import { FurniturePanel, ViewPicker } from './FurniturePalette.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { isAutoCenter, spotInFrontOfDoor, zoneNameSpot } from './zoneLabelPlacement.js';
import { API_BASE, assetUrl } from '../../lib/apiBase.js';
import { Badge, Button, Icon, Input, Modal, Select, Switch, Tabs, Toast, cx, type IconName } from '../ds/index.js';

type ZoneType = 'desk' | 'bubble' | 'door' | 'portal' | 'spawn' | 'meeting-room' | 'desk-area' | 'elevator';

/** Modes de EditorScene. L'interface les regroupe en 4 onglets (voir TABS). */
type Mode = 'select' | 'furniture' | 'zone' | 'wall' | 'door' | 'floor' | 'text' | 'slots';
type Tab = 'select' | 'structure' | 'furniture' | 'organisation';
type StructureMode = 'floor' | 'wall' | 'door';
type OrganisationMode = 'zone' | 'text' | 'slots';

const TABS: Array<{ id: Tab; label: string; icon: IconName; key: string }> = [
  { id: 'select', label: 'Sélection', icon: 'map-pin', key: '1' },
  { id: 'structure', label: 'Structure', icon: 'grid', key: '2' },
  { id: 'furniture', label: 'Mobilier', icon: 'monitor', key: '3' },
  { id: 'organisation', label: 'Organisation', icon: 'map', key: '4' },
];

const tabOf = (mode: Mode): Tab =>
  mode === 'floor' || mode === 'wall' || mode === 'door' ? 'structure'
    : mode === 'zone' || mode === 'text' || mode === 'slots' ? 'organisation'
      : mode;

interface SlotSummary { slots: number; moved: number; unplaced: number; itemsRemoved: number }

/** Résumé d'une (re)génération d'emplacements, pour la notification. */
function slotSummary(r: SlotSummary): string {
  const parts = [`${r.slots} emplacement${r.slots > 1 ? 's' : ''}`];
  if (r.moved > 0) parts.push(`${r.moved} bureau${r.moved > 1 ? 'x' : ''} replacé${r.moved > 1 ? 's' : ''}`);
  if (r.itemsRemoved > 0) parts.push(`${r.itemsRemoved} objet${r.itemsRemoved > 1 ? 's' : ''} retiré${r.itemsRemoved > 1 ? 's' : ''} (ne tenai${r.itemsRemoved > 1 ? 'ent' : 't'} plus)`);
  if (r.unplaced > 0) parts.push(`${r.unplaced} bureau${r.unplaced > 1 ? 'x' : ''} sans place, laissé${r.unplaced > 1 ? 's' : ''} où ${r.unplaced > 1 ? 'ils étaient' : 'il était'}`);
  return parts.join(' · ');
}

/** Traduit une erreur de l'API des emplacements en message affichable. */
async function slotErrorMessage(res: Response): Promise<string> {
  const { error } = await res.json().catch(() => ({ error: '' })) as { error?: string };
  switch (error) {
    case 'SLOT_OVERLAP': return 'Cet emplacement en chevaucherait un autre.';
    case 'DESK_OVERLAP': return 'Un autre bureau est déjà posé là.';
    case 'DESK_ON_OBSTACLE': return 'Il y a un mur, de l\u2019eau, un objet ou une porte à cet endroit.';
    case 'OUT_OF_AREA': return 'L\u2019emplacement doit tenir entièrement dans l\u2019espace bureaux.';
    case 'SLOT_TAKEN': return 'Cet emplacement est pris : le membre doit d\u2019abord libérer son bureau (ou retire-le dans Admin › Bureaux).';
    case 'SLOT_NOT_FOUND': return 'Cet emplacement n\u2019existe plus.';
    case 'AREA_NOT_FOUND': return 'Cet espace bureaux n\u2019existe plus.';
    case 'DESK_NOT_FOUND': return 'Ce bureau n\u2019existe plus.';
    case 'DESK_ON_SLOT': return 'Ce bureau est sur un emplacement : lâche-le sur un emplacement libre, ou déplace son emplacement.';
    default: return `Erreur serveur (HTTP ${res.status}).`;
  }
}

/** Traduit une réponse d'erreur de l'API zones en message affichable. */
async function zoneErrorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json() as { error?: string; conflictWith?: string };
    switch (body.error) {
      case 'ZONE_OVERLAP':
        return `Cette zone chevauche « ${body.conflictWith ?? 'une autre zone'} ». Déplace-la ou redimensionne-la (seules une porte et un ascenseur peuvent chevaucher une bulle ou une salle de réunion).`;
      case 'MISSING_FIELDS':
        return 'Il manque des informations.';
      case 'INVALID_ELEVATOR':
        return 'Ascenseur : indique un groupe (minuscules, chiffres, tirets), un numéro d\u2019étage et un nom à afficher.';
      case 'INVALID_ZONE_TYPE':
        return 'Type de zone inconnu.';
      case 'ZONE_NOT_FOUND':
        return 'Zone introuvable. A-t-elle déjà été supprimée ?';
      case 'DESK_AREA_IN_USE':
        return 'Des bureaux sont posés dans cet espace : il ne peut pas changer de type. Tu peux toujours le déplacer, l\u2019agrandir ou changer la taille des bureaux.';
      default:
        return `Erreur serveur (HTTP ${res.status}).`;
    }
  } catch {
    return `Erreur serveur (HTTP ${res.status}).`;
  }
}

interface MapZone {
  id: string;
  type: string;
  name: string;
  properties: Record<string, unknown>;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** GET /world/desks/personal — voir PersonalDeskService (apps/api). */
interface PersonalDesk {
  id: string;
  areaZoneId: string;
  /** null = posé librement (hors emplacement). */
  slotId: string | null;
  /** Coin haut-gauche relatif à l'espace. */
  x: number;
  y: number;
  displayName: string;
  tileX: number;
  tileY: number;
  width: number;
  height: number;
  outOfArea: boolean;
  items: Array<{ furnitureId: string; dx: number; dy: number; layer: string }>;
}

interface ZoneListEntry {
  slug: string;
  displayName: string;
  /** Carte d'arrivée du campus (celle qu'on ouvre en entrant). */
  isArrival?: boolean;
  /** Fermée aux membres et invités (PUT /world/zones/:slug/disabled). */
  isDisabled?: boolean;
}

// ── Types de zone ────────────────────────────────────────────

const ZONE_TYPES: Array<{ type: ZoneType; name: string; desc: string; color: string }> = [
  { type: 'bubble', name: 'Bulle', desc: 'Discussion à portée de voix', color: 'var(--color-accent)' },
  { type: 'meeting-room', name: 'Salle de réunion', desc: 'Vidéo privée, enregistrable', color: '#5ec8c8' },
  { type: 'desk', name: 'Bureau', desc: 'Place attribuée à quelqu’un', color: 'var(--color-primary-text)' },
  { type: 'desk-area', name: 'Espace bureaux', desc: 'Chacun y pose et aménage son bureau', color: '#f4a261' },
  { type: 'door', name: 'Porte', desc: 'Ferme une salle', color: 'var(--color-warning)' },
  { type: 'portal', name: 'Portail', desc: 'Mène à une autre carte', color: '#c49cf5' },
  { type: 'elevator', name: 'Ascenseur', desc: 'Choisir un étage parmi plusieurs cartes', color: '#8fb4e8' },
  { type: 'spawn', name: 'Point d’arrivée', desc: 'Où l’on apparaît', color: 'var(--color-danger)' },
];
const zoneTypeInfo = (type: string) => ZONE_TYPES.find((t) => t.type === type);

/** Types dont le nom peut être écrit au sol à la création (« Écrire le nom au sol »). */
const NAMEABLE_ON_FLOOR = new Set<ZoneType>(['bubble', 'meeting-room', 'desk', 'portal']);

function PanelHead({ title, help, children }: { title: string; help: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="pl-epanel-head">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h2 className="pl-epanel-title">{title}</h2>
        <p className="pl-help">{help}</p>
      </div>
      {children}
    </div>
  );
}

// ── Organisation › Zones ─────────────────────────────────────

function ZonePanel({ selectedType, onSelectType, zones, onSelectZone, writeName, onWriteNameChange, nameColor, onNameColorChange, sub }: {
  selectedType: ZoneType | null;
  onSelectType: (type: ZoneType | null) => void;
  zones: MapZone[];
  onSelectZone: (zone: MapZone) => void;
  writeName: boolean;
  onWriteNameChange: (v: boolean) => void;
  nameColor: LabelColor;
  onNameColorChange: (c: LabelColor) => void;
  sub: React.ReactNode;
}) {
  const [search, setSearch] = useState('');
  const q = search.trim().toLowerCase();
  const shown = q ? zones.filter((z) => z.name.toLowerCase().includes(q)) : zones;
  return (
    <aside className="pl-epanel" aria-label="Organisation">
      <PanelHead title="Organisation" help="Zones et textes : ce qui donne un rôle et un nom aux lieux.">
        {sub}
        <p className="pl-help" style={{ marginTop: -4 }}>Choisis un type puis trace un rectangle sur la carte.</p>
        <div role="radiogroup" aria-label="Type de zone" className="pl-ztypes">
          {ZONE_TYPES.map((t) => {
            const on = selectedType === t.type;
            return (
              <button key={t.type} type="button" role="radio" aria-checked={on} className="pl-ztype" onClick={() => onSelectType(on ? null : t.type)}>
                <span className="pl-ztype-name"><span className="pl-zdot" style={{ background: t.color }} />{t.name}</span>
                <span className="pl-ztype-desc">{t.desc}</span>
              </button>
            );
          })}
        </div>
        <Switch
          label="Écrire le nom au sol"
          hint="Devant la porte de la salle (au centre tant qu'elle n'a pas de porte), modifiable ensuite."
          checked={writeName}
          onChange={onWriteNameChange}
        />
        {writeName && (
          <div role="radiogroup" aria-label="Couleur du nom" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="pl-help" style={{ marginRight: 2 }}>Couleur</span>
            {(Object.keys(LABEL_COLORS) as LabelColor[]).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={nameColor === k}
                aria-label={LABEL_COLORS[k].label}
                title={LABEL_COLORS[k].label}
                className={cx('pl-swatch', nameColor === k && 'pl-swatch-on')}
                style={{ width: 22, height: 22, background: LABEL_COLORS[k].fill, border: `2px solid ${LABEL_COLORS[k].stroke}` }}
                onClick={() => onNameColorChange(k)}
              />
            ))}
          </div>
        )}
      </PanelHead>
      <div className="pl-epanel-body" style={{ gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <p className="pl-elabel">Zones de la carte</p><span className="pl-count">{zones.length}</span>
        </div>
        {zones.length > 6 && (
          <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une zone" aria-label="Rechercher une zone" />
        )}
        {zones.length === 0 && <p className="pl-help">Aucune zone pour l'instant.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {shown.map((zone) => {
            const info = zoneTypeInfo(zone.type);
            return (
              <button key={zone.id} type="button" className="pl-zrow" onClick={() => onSelectZone(zone)}>
                <span className="pl-zdot" style={{ background: info?.color ?? 'var(--color-text-muted)' }} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{zone.name}</span>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{info?.name ?? zone.type}</span>
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}

// ── Structure › Murs ─────────────────────────────────────────
// Styles LimeZu (Room_Builder, voir wallCatalog.ts) : aperçu d'un pan isolé
// (haut + bas de face, contigus dans l'atlas) découpé en CSS dans l'atlas.

function WallSwatch({ entry }: { entry: WallCatalogEntry }) {
  const { upper, lower } = wallPreviewFrames(entry);
  const ts = WALL_ATLAS.tileSize;
  const tile = (frame: number) => (
    <span style={{
      display: 'block', width: ts, height: ts,
      backgroundImage: `url(${WALL_ATLAS.url})`,
      backgroundPosition: `-${(frame % WALL_ATLAS.columns) * ts}px -${Math.floor(frame / WALL_ATLAS.columns) * ts}px`,
      imageRendering: 'pixelated',
    }} />
  );
  // Mur noir : deux briques sombres (même hauteur d'aperçu que les autres styles).
  return <span style={{ display: 'block' }}>{tile(upper ?? lower)}{tile(lower)}</span>;
}

// Les nôtres (monde du campus, `ch_*` ; murs de la V2 de Copy House en tête) à part de LimeZu.
const OWN_WALLS_FIRST = ['ch_cc-mur', 'ch_cc-noir'];
const ownWalls = wallCatalog.filter((w) => !w.id.startsWith('lz_'))
  .sort((a, b) => rank(OWN_WALLS_FIRST, a.id) - rank(OWN_WALLS_FIRST, b.id));
const WALL_GROUPS = [
  { title: 'Les nôtres', items: ownWalls },
  { title: 'LimeZu', items: wallCatalog.filter((w) => w.id.startsWith('lz_')) },
].filter((g) => g.items.length > 0);
function rank(first: string[], id: string): number {
  const i = first.indexOf(id);
  return i < 0 ? first.length : i;
}

function WallPanel({ selectedId, onSelect, sub }: {
  selectedId: string | null;
  onSelect: (entry: WallCatalogEntry | null) => void;
  sub: React.ReactNode;
}) {
  return (
    <aside className="pl-epanel" aria-label="Structure">
      <PanelHead title="Structure" help={<>Clique ou glisse sur la carte pour peindre un mur, case par case. Clic droit pour l'effacer.</>}>
        {sub}
      </PanelHead>
      <div className="pl-epanel-body">
        {WALL_GROUPS.map((g) => (
          <div key={g.title} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p className="pl-elabel">{g.title}</p>
            <div role="radiogroup" aria-label={g.title} className="pl-swatches" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))' }}>
              {g.items.map((entry) => {
                const on = selectedId === entry.id;
                return (
                  <button key={entry.id} type="button" role="radio" aria-checked={on} aria-label={entry.label} title={entry.label} className="pl-swatch-btn" onClick={() => onSelect(on ? null : entry)}>
                    <span className="pl-swatch-img" style={{ width: 44, height: 72, alignItems: 'center' }}><WallSwatch entry={entry} /></span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <p className="pl-help" style={{ marginTop: 6 }}>
          Un mur horizontal fait 2 cases de haut : peins la case du bas, la partie haute s'ajoute toute seule.
          Un mur vertical apparaît en bande sombre. Angles et jonctions se raccordent automatiquement.
        </p>
      </div>
    </aside>
  );
}

// ── Structure › Sols ─────────────────────────────────────────
// Deux couches peintes (Sol = sols intérieurs et terrains extérieurs raccordés
// deux à deux ; Revêtements = ce qui se pose sur n'importe quel sol), outils de
// peinture, palette découpée en CSS dans l'atlas des sols.

type FloorTool = 'brush' | 'brush3' | 'rect' | 'fill' | 'eraser';

const FLOOR_TOOLS: Array<{ id: FloorTool; label: string; hint: string }> = [
  { id: 'brush', label: 'Pinceau', hint: 'Case par case (cliquer-glisser)' },
  { id: 'brush3', label: 'Large', hint: '3×3 cases (cliquer-glisser)' },
  { id: 'rect', label: 'Rect.', hint: 'Glisser pour remplir un rectangle' },
  { id: 'fill', label: 'Remplir', hint: 'Remplit la zone contiguë de même sol' },
  { id: 'eraser', label: 'Gomme', hint: 'Efface (clic droit = gomme aussi)' },
];

function AtlasTile({ frame, size = 60 }: { frame: number; size?: number }) {
  const ts = FLOOR_ATLAS.tileSize;
  const scale = size / ts;
  const col = frame % FLOOR_ATLAS.columns;
  const row = Math.floor(frame / FLOOR_ATLAS.columns);
  return (
    <span style={{
      display: 'block', width: size, height: size,
      backgroundImage: `url(${FLOOR_ATLAS.url})`,
      backgroundSize: `${FLOOR_ATLAS.columns * ts * scale}px auto`,
      backgroundPosition: `-${col * ts * scale}px -${row * ts * scale}px`,
      imageRendering: 'pixelated',
    }} />
  );
}

function FloorPanel({ layer, onLayerChange, tool, onToolChange, value, onValueChange, sub }: {
  layer: 'floor' | 'overlay';
  onLayerChange: (layer: 'floor' | 'overlay') => void;
  tool: FloorTool;
  onToolChange: (tool: FloorTool) => void;
  value: string | null;
  onValueChange: (value: string | null) => void;
  sub: React.ReactNode;
}) {
  const groups = layer === 'floor'
    ? [
      // Les nôtres (monde du campus, `ch_*`) à part de LimeZu.
      { title: 'Les nôtres', named: true, items: floorMaterials.filter((m) => m.id.startsWith('ch_') && !m.hidden).map((m) => ({ id: m.id, label: m.label, frame: m.frames[0]! })) },
      { title: 'LimeZu, extérieur', named: true, items: floorMaterials.filter((m) => !m.id.startsWith('ch_') && m.group === 'exterior' && !m.hidden).map((m) => ({ id: m.id, label: m.label, frame: m.frames[0]! })) },
      { title: 'LimeZu, intérieur', named: false, items: floorMaterials.filter((m) => !m.id.startsWith('ch_') && m.group === 'interior').map((m) => ({ id: m.id, label: m.label, frame: m.frames[0]! })) },
    ]
    // Aperçu d'un terrain : sa tuile pleine (index 1 du gabarit).
    : [{ title: 'Revêtements', named: true, items: floorTerrains.map((t) => ({ id: t.id, label: t.label, frame: t.fill ?? t.frames[1]! })) }];

  return (
    <aside className="pl-epanel" aria-label="Structure">
      <PanelHead
        title="Structure"
        help={layer === 'floor'
          ? 'Parquet, carrelage, gazon, eau, route… Les terrains extérieurs se raccordent tout seuls (berge, rivage, bordure) : prévois au moins 2 cases de large. Clic droit : effacer.'
          : 'Pelouses bordées et piscine, posées par-dessus n’importe quel sol. Clic droit : effacer.'}
      >
        {sub}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p className="pl-elabel">Couche</p>
          <div><Tabs aria-label="Couche" items={[{ id: 'floor', label: 'Sol' }, { id: 'overlay', label: 'Revêtements' }]} value={layer} onChange={onLayerChange} /></div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p className="pl-elabel">Outil</p>
          <div role="radiogroup" aria-label="Outil" className="pl-tabs" style={{ display: 'flex' }}>
            {FLOOR_TOOLS.map((t) => (
              <button key={t.id} type="button" role="radio" aria-checked={tool === t.id} title={t.hint} className={cx('pl-tab', tool === t.id && 'pl-tab-on')} style={{ flex: 1, padding: '0 6px' }} onClick={() => onToolChange(t.id)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </PanelHead>
      <div className="pl-epanel-body">
        {groups.map((g) => (
          <div key={g.title} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p className="pl-elabel">{g.title}</p>
            <div role="radiogroup" aria-label={g.title} className="pl-swatches">
              {g.items.map((item) => {
                const on = value === item.id && tool !== 'eraser';
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    title={item.label}
                    aria-label={item.label}
                    className="pl-swatch-btn"
                    onClick={() => { onValueChange(item.id); if (tool === 'eraser') onToolChange('brush'); }}
                  >
                    <span className="pl-swatch-img"><AtlasTile frame={item.frame} /></span>
                    {/* Sols intérieurs : noms génériques peu parlants, l'aperçu suffit (nom au survol). */}
                    {g.named && <span className="pl-swatch-name">{item.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
}

// ── Structure › Portes ───────────────────────────────────────
// Portes de DOOR_PALETTE (celles qui s'emboîtent dans un mur de l'éditeur),
// aperçu = première frame (porte fermée) de la spritesheet.

// Porte de la V2 de Copy House (un sprite LimeZu) en tête, puis les autres.
const V2_DOOR = 'door_big_3';
const DOOR_GROUPS = [
  { title: 'Porte de la V2 (Copy House)', items: DOOR_PALETTE.filter((d) => d.id === V2_DOOR) },
  { title: 'LimeZu', items: DOOR_PALETTE.filter((d) => d.id !== V2_DOOR) },
];

function DoorPanel({ selectedId, onSelect, sub }: {
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  sub: React.ReactNode;
}) {
  return (
    <aside className="pl-epanel" aria-label="Structure">
      <PanelHead title="Structure" help="Clique sur la case du bas d'un mur horizontal : la porte remplace le mur à cet endroit. Clic droit sur une porte pour la retirer.">
        {sub}
      </PanelHead>
      <div className="pl-epanel-body">
        {DOOR_GROUPS.map((g) => (
          <div key={g.title} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p className="pl-elabel">{g.title}</p>
            <div role="radiogroup" aria-label={g.title} className="pl-swatches" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              {g.items.map(({ id, label }) => {
                const door = findDoor(id);
                if (!door) return null;
                const on = selectedId === id;
                return (
                  <button key={id} type="button" role="radio" aria-checked={on} aria-label={label} title={label} className="pl-swatch-btn" onClick={() => onSelect(on ? null : id)}>
                    <span className="pl-swatch-img" style={{ width: '100%', height: 108, padding: 4, boxSizing: 'border-box' }}>
                      <span style={{
                        display: 'block', width: door.frameWidth, height: door.frameHeight,
                        backgroundImage: `url(${door.url})`, backgroundPosition: '0 0',
                        imageRendering: 'pixelated',
                      }} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <p className="pl-help" style={{ marginTop: 6 }}>
          Une porte qui donne sur une bulle ou une salle de réunion y est liée automatiquement : elle se ferme avec la salle
          (les autres doivent toquer). Pour changer ça : Organisation › Zones, clic sur la porte.
        </p>
      </div>
    </aside>
  );
}

// ── Organisation › Emplacements ──────────────────────────────

interface DeskSlot {
  id: string;
  areaZoneId: string;
  x: number; y: number;
  tileX: number; tileY: number;
  width: number; height: number;
  outOfArea: boolean;
  deskId: string | null;
  ownerName: string | null;
}

function SlotPanel({ areas, slots, deskCountByArea, freeDeskCountByArea, onRegenerate, onPlaceDesks, busy, sub }: {
  areas: MapZone[];
  slots: DeskSlot[];
  /** Bureaux posés (sur un emplacement ou librement) par espace. */
  deskCountByArea: Map<string, number>;
  /** Bureaux posés librement (hors emplacement) par espace. */
  freeDeskCountByArea: Map<string, number>;
  onRegenerate: (areaId: string) => void;
  onPlaceDesks: (areaId: string) => void;
  busy: boolean;
  sub: React.ReactNode;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  return (
    <aside className="pl-epanel" aria-label="Organisation">
      <PanelHead title="Organisation" help="Emplacements de bureaux : chaque membre en prend un dans un espace bureaux, puis l'aménage.">
        {sub}
      </PanelHead>
      <div className="pl-epanel-body">
        <div className="pl-help">
          <ol>
            <li>Clique sur une case vide d'un espace bureaux pour y ajouter un emplacement (coin haut-gauche).</li>
            <li>Glisse un emplacement pour le déplacer : le bureau posé dessus suit.</li>
            <li>Glisse un bureau sur un emplacement libre (il devient vert) pour l'y ranger, aménagement compris.</li>
            <li>Clic droit sur un emplacement libre pour le retirer.</li>
          </ol>
        </div>
        {areas.length === 0 ? (
          <p className="pl-help">Aucun espace bureaux sur cette carte. Crée-en un dans Organisation › Zones.</p>
        ) : areas.map((area) => {
          const mine = slots.filter((sl) => sl.areaZoneId === area.id && !sl.outOfArea);
          const taken = mine.filter((sl) => sl.deskId).length;
          const desks = deskCountByArea.get(area.id) ?? 0;
          const freeDesks = freeDeskCountByArea.get(area.id) ?? 0;
          const freeSlots = mine.length - taken;
          const size = deskSizeOf(area.properties);
          return (
            <div key={area.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 0', borderTop: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <p className="pl-elabel" style={{ margin: 0, flex: 1 }}>{area.name}</p>
                <span className="pl-help">bureaux {size.width}×{size.height}</span>
              </div>
              <p className="pl-help" style={{ margin: 0 }}>
                {mine.length === 0 ? 'Aucun emplacement : les membres ne peuvent pas encore y prendre de bureau.'
                  : `${mine.length} emplacement${mine.length > 1 ? 's' : ''}, ${taken} pris`}
              </p>
              {freeDesks > 0 && (
                <>
                  <p className="pl-help" style={{ margin: 0 }}>
                    {freeDesks > 1 ? `${freeDesks} bureaux sont posés` : '1 bureau est posé'} hors emplacement.
                    {freeSlots === 0 ? ' Ajoute des emplacements libres pour les y placer.' : ''}
                  </p>
                  <Button variant="primary" size="sm" icon="map-pin" disabled={busy || freeSlots === 0}
                    onClick={() => onPlaceDesks(area.id)}>
                    {freeDesks > 1 ? 'Placer ces bureaux' : 'Placer ce bureau'} sur les emplacements libres
                  </Button>
                </>
              )}
              {confirmId === area.id ? (
                <>
                  <div className="pl-alert" role="alert">
                    Les emplacements seront recréés. {desks > 0
                      ? `${desks > 1 ? `Les ${desks} bureaux posés seront replacés` : 'Le bureau posé sera replacé'} au plus près de ${desks > 1 ? 'leur' : 'sa'} place actuelle.`
                      : 'Aucun bureau n\u2019est posé ici.'}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)}>Annuler</Button>
                    <Button variant="primary" size="sm" disabled={busy} onClick={() => { setConfirmId(null); onRegenerate(area.id); }}>Régénérer</Button>
                  </div>
                </>
              ) : (
                <Button variant="secondary" size="sm" icon="grid" disabled={busy}
                  onClick={() => { if (mine.length === 0 && desks === 0) onRegenerate(area.id); else setConfirmId(area.id); }}>
                  {mine.length === 0 ? 'Générer les emplacements' : 'Régénérer les emplacements'}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

// ── Organisation › Textes ────────────────────────────────────

function TextPanel({ count, sub }: { count: number; sub: React.ReactNode }) {
  return (
    <aside className="pl-epanel" aria-label="Organisation">
      <PanelHead title="Organisation" help="Zones et textes : ce qui donne un rôle et un nom aux lieux.">
        {sub}
      </PanelHead>
      <div className="pl-epanel-body">
        <div className="pl-help">
          <ol>
            <li>Clique sur une case vide pour ajouter un texte (nom de salle, indication…).</li>
            <li>Clique sur un texte pour le modifier ou le supprimer.</li>
            <li>Glisse un texte pour le déplacer.</li>
          </ol>
        </div>
        <p className="pl-help">Le texte est posé au sol : les avatars et le mobilier passent par-dessus.</p>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <p className="pl-elabel">Textes sur cette carte</p><span className="pl-count">{count}</span>
        </div>
      </div>
    </aside>
  );
}

function LabelModal({ label, onSave, onDelete, onCancel, error }: {
  label: { text: string; size: LabelSize; color: LabelColor; id?: string };
  onSave: (data: { text: string; size: LabelSize; color: LabelColor }) => void | Promise<void>;
  onDelete?: (() => void | Promise<void>) | undefined;
  onCancel: () => void;
  error: string | null;
}) {
  const [text, setText] = useState(label.text);
  const [size, setSize] = useState<LabelSize>(label.size);
  const [color, setColor] = useState<LabelColor>(label.color);
  const c = LABEL_COLORS[color];
  const submit = () => { if (text.trim()) void onSave({ text: text.trim(), size, color }); };

  return (
    <Modal
      title={label.id ? 'Modifier le texte' : 'Nouveau texte'}
      onClose={onCancel}
      footer={
        <>
          {onDelete && <Button variant="danger" onClick={() => { void onDelete(); }} style={{ marginRight: 'auto' }}>Supprimer</Button>}
          <Button variant="ghost" onClick={onCancel}>Annuler</Button>
          <Button variant="primary" onClick={submit} disabled={!text.trim()}>{label.id ? 'Enregistrer' : 'Ajouter'}</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--color-text-primary)' }}>
        <Input
          label="Texte"
          autoFocus
          value={text}
          maxLength={MAX_LABEL_LENGTH}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Salle Ascension"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="pl-field-label">Taille</span>
          <div><Tabs aria-label="Taille" items={(Object.keys(LABEL_SIZES) as LabelSize[]).map((k) => ({ id: k, label: LABEL_SIZES[k].label }))} value={size} onChange={setSize} /></div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span className="pl-field-label">Couleur</span>
          <div role="radiogroup" aria-label="Couleur" style={{ display: 'flex', gap: 10 }}>
            {(Object.keys(LABEL_COLORS) as LabelColor[]).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={color === k}
                aria-label={LABEL_COLORS[k].label}
                title={LABEL_COLORS[k].label}
                className={cx('pl-swatch', color === k && 'pl-swatch-on')}
                style={{ width: 30, height: 30, background: LABEL_COLORS[k].fill, border: `2px solid ${LABEL_COLORS[k].stroke}` }}
                onClick={() => setColor(k)}
              />
            ))}
          </div>
        </div>
        {/* Aperçu sur fond parquet et gazon — les deux sols les plus courants. */}
        <div style={{ display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          {['#b98352', '#479757'].map((bg) => (
            <div key={bg} style={{ flex: 1, background: bg, padding: '10px 4px', textAlign: 'center' }}>
              <span style={{
                fontFamily: 'Arial, Helvetica, sans-serif', fontWeight: 700,
                fontSize: LABEL_SIZES[size].fontSize, color: c.fill,
                WebkitTextStroke: `${Math.max(1, LABEL_SIZES[size].fontSize / 10)}px ${c.stroke}`,
                paintOrder: 'stroke fill',
              }}>
                {text.trim() || 'Aperçu'}
              </span>
            </div>
          ))}
        </div>
        {error && <div className="pl-alert" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}

// ── Nouvelle carte vierge ────────────────────────────────────
// Carte vierge (POST /world/zones/blank) : tout se construit ensuite dans
// l'éditeur — sols peints, murs, portes, mobilier, zones.

function roleFromJwt(jwt: string | null): string {
  if (!jwt) return '';
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return String(payload.role ?? '').toUpperCase();
  } catch { return ''; }
}

function slugify(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
}

function NewMapModal({ existingSlugs, onCreated, onCancel }: {
  existingSlugs: string[];
  onCreated: (slug: string, displayName: string) => void;
  onCancel: () => void;
}) {
  // Même en-têtes que le reste de l'éditeur : sans X-Tenant-ID, l'API répond 400 TENANT_REQUIRED.
  const authHeaders = useAuthHeaders(getTenantSlug());
  const [name, setName] = useState('');
  const [width, setWidth] = useState(60);
  const [height, setHeight] = useState(40);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const slug = slugify(name);

  const create = async () => {
    setError(null);
    if (!slug) { setError('Donne un nom à la carte.'); return; }
    if (existingSlugs.includes(slug)) { setError(`Une carte « ${slug} » existe déjà. Choisis un autre nom.`); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/world/zones/blank`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, displayName: name.trim(), width, height }),
      });
      if (res.status === 409) { setError(`Une carte « ${slug} » existe déjà. Choisis un autre nom.`); return; }
      if (!res.ok) { setError(`Erreur serveur (HTTP ${res.status}).`); return; }
      onCreated(slug, name.trim());
    } catch {
      setError('Erreur réseau : la carte n’a pas été créée.');
    } finally {
      setBusy(false);
    }
  };

  const clamp = (v: string) => Math.max(10, Math.min(150, Number(v) || 10));

  return (
    <Modal
      title="Nouvelle carte"
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>Annuler</Button>
          <Button variant="primary" onClick={() => { void create(); }} disabled={busy}>{busy ? 'Création…' : 'Créer la carte'}</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--color-text-primary)' }}>
        <Input label="Nom" value={name} onChange={(e) => setName(e.target.value)} placeholder="Bureaux du 2e étage" autoFocus hint={`Identifiant : ${slug || '-'}`} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
          <Input label="Largeur (cases)" type="number" min={10} max={150} value={width} onChange={(e) => setWidth(clamp(e.target.value))} />
          <Input label="Hauteur (cases)" type="number" min={10} max={150} value={height} onChange={(e) => setHeight(clamp(e.target.value))} />
        </div>
        <p className="pl-help">De 10 à 150 cases. La taille ne pourra pas être changée ensuite. Pour démarrer : Structure › Sols › Remplir.</p>
        {error && <div className="pl-alert" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}

// ── Suppression d'une carte ──────────────────────────────────
// OWNER seulement. Aperçu (GET /world/zones/:slug/delete-preview) puis
// DELETE /world/zones/:slug — voir ZoneDeletionService (apps/api).

interface ZoneDeletionPreview {
  slug: string;
  displayName: string;
  takenDesks: number;
  deskSlots: number;
  deskAssignments: number;
  furniture: number;
  walls: number;
  labels: number;
  mapZones: number;
  portalsFrom: Array<{ mapSlug: string; mapName: string; name: string }>;
  profiles: Array<{ id: string; name: string; memberCount: number; onlyMap: boolean }>;
  recordings: number;
  inviteLinks: number;
  blocker: 'ARRIVAL_MAP' | 'ONLY_MAP_OF_PROFILE' | 'RECORDING_IN_PROGRESS' | null;
}

const DELETE_BLOCKERS: Record<string, string> = {
  ARRIVAL_MAP: 'C’est la carte d’arrivée du campus. Choisis d’abord une autre carte d’arrivée.',
  ONLY_MAP_OF_PROFILE: 'C’est la seule carte d’un profil d’accès : ses membres n’auraient plus aucune carte. Ajoute d’abord une autre carte à ce profil (Admin › Membres › Accès).',
  RECORDING_IN_PROGRESS: 'Un enregistrement est en cours dans une salle de cette carte. Attends qu’il soit terminé.',
};

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

function DeleteMapModal({ slug, onDeleted, onCancel }: {
  slug: string;
  onDeleted: () => void;
  onCancel: () => void;
}) {
  const authHeaders = useAuthHeaders(getTenantSlug());
  const [preview, setPreview] = useState<ZoneDeletionPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/world/zones/${encodeURIComponent(slug)}/delete-preview`, { headers: authHeaders })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json() as ZoneDeletionPreview;
        if (!cancelled) setPreview(data);
      })
      .catch(() => { if (!cancelled) setError('Impossible de savoir ce que contient la carte. Réessaie.'); });
    return () => { cancelled = true; };
  }, [slug]);

  const remove = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/world/zones/${encodeURIComponent(slug)}`, { method: 'DELETE', headers: authHeaders });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        setError(DELETE_BLOCKERS[body.error ?? ''] ?? 'La carte ne peut pas être supprimée.');
        return;
      }
      if (!res.ok) { setError(`Erreur serveur (HTTP ${res.status}).`); return; }
      onDeleted();
    } catch {
      setError('Erreur réseau : la carte n’a pas été supprimée.');
    } finally {
      setBusy(false);
    }
  };

  const lines: string[] = [];
  if (preview) {
    const built = [
      preview.furniture && plural(preview.furniture, 'objet', 'objets'),
      preview.walls && plural(preview.walls, 'mur', 'murs'),
      preview.mapZones && plural(preview.mapZones, 'zone', 'zones'),
      preview.labels && plural(preview.labels, 'texte', 'textes'),
    ].filter(Boolean);
    if (built.length) lines.push(`Sols, ${built.join(', ')} seront supprimés.`);
    if (preview.takenDesks) lines.push(`${plural(preview.takenDesks, 'bureau pris par un membre sera libéré', 'bureaux pris par des membres seront libérés')}, aménagement compris.`);
    if (preview.deskSlots) lines.push(`${plural(preview.deskSlots, 'emplacement de bureau sera supprimé', 'emplacements de bureau seront supprimés')}.`);
    if (preview.deskAssignments) lines.push(`${plural(preview.deskAssignments, 'bureau réservé sera libéré', 'bureaux réservés seront libérés')}.`);
    for (const p of preview.portalsFrom) lines.push(`Le portail « ${p.name || 'sans nom'} » de la carte ${p.mapName} sera supprimé.`);
    for (const p of preview.profiles.filter((x) => !x.onlyMap)) {
      lines.push(`Le profil d’accès « ${p.name} » n’aura plus cette carte${p.memberCount ? ` : ${plural(p.memberCount, 'membre sera déconnecté', 'membres seront déconnectés')} et devra se reconnecter` : ''}.`);
    }
    if (preview.inviteLinks) lines.push(`${plural(preview.inviteLinks, 'lien invité limité à cette carte sera supprimé', 'liens invités limités à cette carte seront supprimés')}.`);
    if (preview.recordings) lines.push(`${plural(preview.recordings, 'enregistrement est gardé', 'enregistrements sont gardés')} dans Admin › Enregistrements.`);
  }

  const blocker = preview?.blocker ? DELETE_BLOCKERS[preview.blocker] : null;

  return (
    <Modal
      title={`Supprimer la carte${preview ? ` « ${preview.displayName} »` : ''}`}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>Annuler</Button>
          <Button variant="danger" onClick={() => { void remove(); }} disabled={busy || !preview || !!blocker}>
            {busy ? 'Suppression…' : 'Supprimer définitivement'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--color-text-primary)' }}>
        {!preview && !error && <p className="pl-help">Chargement…</p>}
        {blocker && <div className="pl-alert" role="alert">{blocker}</div>}
        {preview && !blocker && (
          <>
            <p>La carte et tout ce qui est posé dessus seront supprimés. Impossible de revenir en arrière.</p>
            {lines.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {lines.map((l) => <li key={l}>{l}</li>)}
              </ul>
            )}
          </>
        )}
        {error && <div className="pl-alert" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}

// ── Espace bureaux ───────────────────────────────────────────

/** Emplacements générés dans l'espace vide (rangées dos à dos avec allées) — moins s'il y a des meubles. */
const deskCapacity = (area: { width: number; height: number }, w: number, h: number) =>
  generateDeskSlots({ x: 0, y: 0, width: area.width, height: area.height }, { width: w, height: h }).length;

const sizeOptions = (min: number, max: number) =>
  Array.from({ length: max - min + 1 }, (_, i) => ({ value: String(min + i), label: `${min + i} cases` }));

function DeskAreaFields({ zone, deskWidth, deskHeight, onChange, deskCount, confirmDelete }: {
  zone: { width: number; height: number; id?: string; properties?: Record<string, unknown> };
  deskWidth: number;
  deskHeight: number;
  onChange: (w: number, h: number) => void;
  deskCount: number;
  confirmDelete: boolean;
}) {
  const capacity = deskCapacity(zone, deskWidth, deskHeight);
  const plural = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''}`;
  const saved = zone.id ? deskSizeOf(zone.properties) : null;
  const sizeChanged = !!saved && (saved.width !== deskWidth || saved.height !== deskHeight);
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        <Select
          label="Largeur d'un bureau"
          value={String(deskWidth)}
          onChange={(e) => onChange(Number(e.target.value), deskHeight)}
          options={sizeOptions(MIN_DESK_SIZE.width, MAX_DESK_SIZE.width)}
        />
        <Select
          label="Hauteur d'un bureau"
          value={String(deskHeight)}
          onChange={(e) => onChange(deskWidth, Number(e.target.value))}
          options={sizeOptions(MIN_DESK_SIZE.height, MAX_DESK_SIZE.height)}
        />
      </div>
      {capacity === 0 ? (
        <div className="pl-alert" role="alert">L'espace est trop petit pour un bureau de {deskWidth}×{deskHeight} cases.</div>
      ) : (
        <p className="pl-help">
          L'espace est rempli d'emplacements de {deskWidth}×{deskHeight} cases, en rangées avec une allée :
          jusqu'à {plural(capacity, 'emplacement')} (moins s'il y a des meubles). Chaque membre en prend un puis
          l'aménage. Tu peux les retoucher dans Organisation › Emplacements.
        </p>
      )}
      {sizeChanged && deskCount > 0 && (
        <div className="pl-alert" role="alert">
          Les emplacements seront recréés : {deskCount > 1 ? `les ${deskCount} bureaux posés seront replacés` : 'le bureau posé sera replacé'} au
          plus près de {deskCount > 1 ? 'leur' : 'sa'} place actuelle. Les objets qui ne tiennent plus dans la nouvelle taille seront retirés.
        </div>
      )}
      {confirmDelete && (
        <div className="pl-alert" role="alert">
          Supprimer l'espace supprime aussi {deskCount > 1 ? `les ${deskCount} bureaux` : 'le bureau'} et {deskCount > 1 ? 'leur' : 'son'} aménagement.
        </div>
      )}
    </>
  );
}

// ── Propriétés d'une zone ────────────────────────────────────

function ZonePropertiesModal({ zone, onSave, onDelete, onCancel, error, availableZones, bubbleNames, deskCount = 0 }: {
  zone: { type: ZoneType; name?: string; properties?: Record<string, unknown>; x: number; y: number; width: number; height: number; id?: string };
  onSave: (data: { name: string; properties: Record<string, unknown> }) => void | Promise<void>;
  onDelete?: (() => void | Promise<void>) | undefined;
  onCancel: () => void;
  error?: string | null;
  availableZones: ZoneListEntry[];
  /** Noms des bulles/salles de réunion de la carte — cibles possibles d'une porte. */
  bubbleNames: string[];
  /** Espace bureaux : bureaux déjà posés par des membres. */
  deskCount?: number;
}) {
  const isEdit = !!zone.id;
  const [name, setName] = useState(zone.name ?? '');
  const [props, setProps] = useState<Record<string, unknown>>(() => (
    zone.type === 'desk-area' ? { deskWidth: DEFAULT_DESK_SIZE.width, deskHeight: DEFAULT_DESK_SIZE.height, ...zone.properties } : zone.properties ?? {}
  ));
  // Suppression d'un espace occupé : second clic pour confirmer.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const updateProp = (key: string, value: unknown) => setProps((p) => ({ ...p, [key]: value }));
  const info = zoneTypeInfo(zone.type);
  const str = (key: string) => (props[key] as string) ?? '';

  const doorSprite = str('doorSprite');
  const linkedBubble = str('linkedBubble');

  return (
    <Modal
      title={`${isEdit ? 'Modifier' : 'Nouvelle zone'} · ${info?.name ?? zone.type}`}
      onClose={onCancel}
      footer={
        <>
          {isEdit && onDelete && (
            <Button
              variant="danger"
              onClick={() => { if (deskCount > 0 && !confirmDelete) setConfirmDelete(true); else void onDelete(); }}
              style={{ marginRight: 'auto' }}
            >
              {confirmDelete ? 'Confirmer la suppression' : 'Supprimer'}
            </Button>
          )}
          <Button variant="ghost" onClick={onCancel}>Annuler</Button>
          <Button variant="primary" disabled={!name.trim()} onClick={() => { if (name.trim()) void onSave({ name: name.trim(), properties: props }); }}>
            {isEdit ? 'Enregistrer' : 'Créer la zone'}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (name.trim()) void onSave({ name: name.trim(), properties: props }); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14, color: 'var(--color-text-primary)' }}
      >
        <Input
          label="Nom"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={zone.type === 'spawn' ? 'default' : 'Salle Ascension'}
          autoFocus
          {...(zone.type === 'spawn' ? { hint: '« default » = point d’arrivée de la carte. Un autre nom sert de cible à un portail.' } : {})}
        />

        {zone.type === 'desk' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <Input label="Identifiant du bureau" value={str('deskId')} onChange={(e) => updateProp('deskId', e.target.value)} placeholder="desk-01" />
            <Input label="Nom affiché" value={str('label')} onChange={(e) => updateProp('label', e.target.value)} placeholder="Bureau près de la fenêtre" />
          </div>
        )}

        {zone.type === 'desk-area' && (
          <DeskAreaFields
            zone={zone}
            deskWidth={deskSizeOf(props).width}
            deskHeight={deskSizeOf(props).height}
            onChange={(w, h) => setProps((p) => ({ ...p, deskWidth: w, deskHeight: h }))}
            deskCount={deskCount}
            confirmDelete={confirmDelete}
          />
        )}

        {(zone.type === 'bubble' || zone.type === 'meeting-room') && (
          <>
            <div style={{ width: 140 }}>
              <Input label="Capacité" type="number" min={1} value={(props.capacity as number) ?? 4} onChange={(e) => updateProp('capacity', Number(e.target.value))} />
            </div>
            <Switch
              label="Revendicable par un duo"
              hint="Les deux personnes seules dans la salle peuvent la revendiquer jusqu'au dimanche suivant inclus : elle et son tableau blanc ne s'ouvrent alors qu'à elles (et aux admins, qui peuvent la libérer)."
              checked={props.claimable === true}
              onChange={(on) => updateProp('claimable', on || undefined)}
            />
            {zone.type === 'meeting-room' && (
              <>
                <p className="pl-help">Vidéo séparée du reste de la carte : la réunion peut être enregistrée par un admin présent dans la salle.</p>
                <Select
                  label="Type de séance"
                  value={str('sessionType')}
                  onChange={(e) => updateProp('sessionType', e.target.value || undefined)}
                  hint="Quand un enregistrement démarre, ceux qui peuvent entrer dans la salle voient « Coaching en cours - nom de la salle » et peuvent la rejoindre d'un clic."
                  options={[
                    { value: '', label: 'Aucun (« Séance en cours »)' },
                    ...SESSION_TYPES.map((t) => ({ value: t.id, label: t.label })),
                  ]}
                />
              </>
            )}
          </>
        )}

        {zone.type === 'door' && (
          <>
            <Select
              label="Apparence"
              value={doorSprite}
              onChange={(e) => updateProp('doorSprite', e.target.value)}
              options={[
                { value: '', label: 'Aucune (passage invisible)' },
                ...DOOR_PALETTE.map((d) => ({ value: d.id, label: d.label })),
                // Sprite hors palette déjà utilisé par une porte existante : le garder sélectionnable.
                ...(doorSprite && !DOOR_PALETTE.some((d) => d.id === doorSprite) ? [{ value: doorSprite, label: doorSprite }] : []),
              ]}
            />
            <Select
              label="Salle liée"
              value={linkedBubble}
              onChange={(e) => updateProp('linkedBubble', e.target.value)}
              hint="Une porte liée se ferme avec la salle : les autres doivent toquer."
              options={[
                { value: '', label: 'Aucune (porte simple, jamais fermée à clé)' },
                ...bubbleNames.map((n) => ({ value: n, label: n })),
                ...(linkedBubble && !bubbleNames.includes(linkedBubble) ? [{ value: linkedBubble, label: `${linkedBubble} (introuvable)` }] : []),
              ]}
            />
          </>
        )}

        {zone.type === 'elevator' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px', gap: 12 }}>
              <Input label="Groupe" value={str('group')} onChange={(e) => updateProp('group', e.target.value.toLowerCase())} placeholder="copy-camp" hint="Même groupe sur chaque étage desservi." />
              <Input label="Étage" type="number" min={-5} max={50} value={(props.floor as number | undefined) ?? 0} onChange={(e) => updateProp('floor', Math.trunc(Number(e.target.value)))} hint="0 = rez-de-chaussée" />
            </div>
            <Input label="Nom dans la liste" value={str('label')} onChange={(e) => updateProp('label', e.target.value)} placeholder="1er étage - Travaux en duo" />
            <p className="pl-help">À poser comme une porte, sur la rangée bloquante d'un mur horizontal (2 cases de large). En s'arrêtant devant, on choisit un des autres étages du groupe et on arrive devant leur ascenseur.</p>
          </>
        )}

        {zone.type === 'portal' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            <Select
              label="Carte cible"
              value={str('targetSlug')}
              onChange={(e) => updateProp('targetSlug', e.target.value)}
              options={[{ value: '', label: 'Choisir…' }, ...availableZones.map((z) => ({ value: z.slug, label: z.displayName }))]}
            />
            <Input label="Point d'arrivée" value={str('spawnId')} onChange={(e) => updateProp('spawnId', e.target.value)} placeholder="default" hint="Nom du point d'arrivée sur la carte cible." />
          </div>
        )}

        <p className="pl-help" style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          Case ({zone.x}, {zone.y}) · {zone.width}×{zone.height} cases
        </p>

        {error && <div className="pl-alert" role="alert">{error}</div>}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/**
 * EditorPage — éditeur de carte (admin, ordinateur uniquement).
 * 4 onglets (raccourcis 1–4) : Sélection, Structure (Sols · Murs · Portes,
 * S/M/P), Mobilier (Objets · Planches · Animés), Organisation (Zones ·
 * Textes, Z/T). Chaque modification est enregistrée tout de suite.
 */
export function EditorPage() {
  const jwt = useJwt();
  const authHeaders = useAuthHeaders(getTenantSlug());
  const canvasRef = useRef<HTMLDivElement>(null);
  const gameRef = useRef<Phaser.Game | null>(null);

  const [zones, setZones] = useState<ZoneListEntry[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<Mode>('select');
  const [sceneReady, setSceneReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFurnitureId, setSelectedFurnitureId] = useState<string | null>(null);
  const [furnitureLayer, setFurnitureLayer] = useState<string>('furniture');
  const [furnitureIsSolid, setFurnitureIsSolid] = useState(false);
  // Bloquant : seulement la rangée du bas (l'avatar passe derrière le haut — portes, armoires).
  const [furnitureSolidBaseOnly, setFurnitureSolidBaseOnly] = useState(false);
  // Meuble posé sélectionné sur la carte (clic dessus) : sa vue se change par R ou la barre.
  const [placedSelection, setPlacedSelection] = useState<{ id: string; furnitureId: string } | null>(null);

  // Zone state (ME-3c/e/f)
  const [selectedZoneType, setSelectedZoneType] = useState<ZoneType | null>(null);
  const [mapZones, setMapZones] = useState<MapZone[]>([]);
  const [zoneModal, setZoneModal] = useState<{
    type: ZoneType; x: number; y: number; width: number; height: number;
    id?: string; name?: string; properties?: Record<string, unknown>;
  } | null>(null);
  const [zoneModalError, setZoneModalError] = useState<string | null>(null);
  const [selectedWallStyleId, setSelectedWallStyleId] = useState<string | null>(null);
  const [selectedDoorId, setSelectedDoorId] = useState<string | null>(null);
  const [floorLayer, setFloorLayer] = useState<'floor' | 'overlay'>('floor');
  const [floorTool, setFloorTool] = useState<FloorTool>('brush');
  const [floorValue, setFloorValue] = useState<string | null>(null);
  const [newMapOpen, setNewMapOpen] = useState(false);
  const [deleteMapOpen, setDeleteMapOpen] = useState(false);
  const isOwner = roleFromJwt(jwt) === 'OWNER';
  const [labels, setLabels] = useState<MapLabel[]>([]);
  // Lu par les gestionnaires d'événements de l'éditeur (porte posée), sans dépendance d'effet.
  const labelsRef = useRef<MapLabel[]>([]);
  labelsRef.current = labels;
  const [labelModal, setLabelModal] = useState<{ tileX: number; tileY: number; label: MapLabel | null } | null>(null);
  const [labelModalError, setLabelModalError] = useState<string | null>(null);
  const [editorToast, setEditorToast] = useState<string | null>(null);
  // Bureaux posés par les membres (lecture seule ici) ; recharge = +1.
  const [personalDesks, setPersonalDesks] = useState<PersonalDesk[]>([]);
  const [deskSlots, setDeskSlots] = useState<DeskSlot[]>([]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [desksVersion, setDesksVersion] = useState(0);
  // Position d'origine à restaurer visuellement si un glisser-déposer en
  // cours est annulé (voir editor:zone-move-preview / editor:zone-move-rejected).
  const [zoneRevertPos, setZoneRevertPos] = useState<{ id: string; x: number; y: number } | null>(null);

  // Dernier sous-mode de chaque onglet (Structure, Organisation), repris au retour sur l'onglet.
  const [structureMode, setStructureMode] = useState<StructureMode>('floor');
  const [organisationMode, setOrganisationMode] = useState<OrganisationMode>('zone');
  // Organisation › Zones : écrire le nom de la zone au sol à sa création.
  const [writeZoneName, setWriteZoneName] = useState(true);
  const [zoneNameColor, setZoneNameColor] = useState<LabelColor>('white');

  // Tout est enregistré au fil de l'eau (API) : isDirty indique seulement
  // qu'une modification a eu lieu, pour afficher « Enregistré ».
  const [isDirty, setIsDirty] = useState(false);

  // Preview mode (ME-4c)
  const [isPreview, setIsPreview] = useState(false);
  const modeBeforePreviewRef = useRef<Mode>('select');

  // ── Désactiver / réactiver la carte choisie ──────────────────
  const toggleMapDisabled = async (slug: string) => {
    const disabled = !zones.find((z) => z.slug === slug)?.isDisabled;
    try {
      const res = await fetch(`${API_BASE}/world/zones/${encodeURIComponent(slug)}/disabled`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ disabled }),
      });
      if (res.status === 409) {
        setEditorToast('La carte d’arrivée ne peut pas être désactivée : choisis d’abord une autre carte d’arrivée.');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setZones((prev) => prev.map((z) => (z.slug === slug ? { ...z, isDisabled: disabled } : z)));
      setEditorToast(disabled
        ? 'Carte désactivée : les membres et les invités présents sont renvoyés à l’arrivée.'
        : 'Carte réactivée : tout le monde peut y aller.');
    } catch (err) {
      console.warn('[EditorPage] désactivation de la carte:', err);
      setEditorToast('La carte n’a pas pu être modifiée. Réessaie.');
    }
  };

  // ── Fetch zone list ────────────────────────────────────────
  useEffect(() => {
    if (!jwt) return;

    fetch(`${API_BASE}/world/zones`, { headers: authHeaders })
      .then((r) => r.json())
      .then((data: ZoneListEntry[]) => {
        setZones(data);
        if (data.length > 0 && !selectedSlug) {
          setSelectedSlug(data[0]!.slug);
        }
      })
      .catch((err) => console.warn('[EditorPage] zone list failed:', err));
  }, [jwt]);

  // ── Load zone into Phaser ──────────────────────────────────
  const loadZone = useCallback(async (slug: string) => {
    if (!jwt || !canvasRef.current) return;
    setLoading(true);
    setSceneReady(false);
    setIsDirty(false);

    try {
      const [configRes, assetsRes] = await Promise.all([
        fetch(`${API_BASE}/world/zones/${encodeURIComponent(slug)}/config`, { headers: authHeaders }),
        fetch(`${API_BASE}/world/zones/${encodeURIComponent(slug)}/assets`, { headers: authHeaders }),
      ]);

      if (!configRes.ok || !assetsRes.ok) throw new Error('HTTP error');

      const zoneConfig = await configRes.json() as ZoneConfig;
      const urls = await assetsRes.json() as ZoneAssetUrls;

      // urls.tmj/wam sont des chemins relatifs à /assets/tenants/:tenantId/* —
      // il faut les rendre absolus (API_BASE, domaine différent en prod),
      // comme fait dans BootScene.ts pour ZoneScene.
      urls.tmj = assetUrl(urls.tmj);
      urls.wam = assetUrl(urls.wam);

      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }

      const container = canvasRef.current;
      const game = new Phaser.Game({
        type: Phaser.AUTO,
        parent: container,
        width: container.clientWidth,
        height: container.clientHeight,
        backgroundColor: '#0D0F14',
        scene: [],
        scale: {
          mode: Phaser.Scale.RESIZE,
          autoCenter: Phaser.Scale.CENTER_BOTH,
        },
      });

      gameRef.current = game;
      applyPixelFiltering(game);

      game.events.once(Phaser.Core.Events.READY, () => {
        game.scene.add('EditorScene', EditorScene, true, {
          zoneConfig,
          urls,
        });
      });
    } catch (err) {
      console.error('[EditorPage] load zone failed:', err);
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  // ── Load selected zone ─────────────────────────────────────
  useEffect(() => {
    if (selectedSlug) loadZone(selectedSlug);
  }, [selectedSlug, loadZone]);

  // ── Listen for scene ready ─────────────────────────────────
  useEffect(() => {
    const handler = () => setSceneReady(true);
    EventBus.on('editor:scene-ready', handler);
    return () => { EventBus.off('editor:scene-ready', handler); };
  }, []);

  // ── Zone events (ME-3) ───────────────────────────────────
  useEffect(() => {
    const onZonesLoaded = ({ zones }: { zones: MapZone[] }) => {
      setMapZones(zones);
    };
    const onZoneDrawn = (data: { type: ZoneType; x: number; y: number; width: number; height: number }) => {
      setZoneModalError(null);
      setZoneModal(data);
      setZoneRevertPos(null);
    };
    const onZoneSelected = (data: MapZone) => {
      setZoneModalError(null);
      setZoneModal({ ...data, type: data.type as ZoneType, id: data.id });
      setZoneRevertPos(null);
    };

    EventBus.on('editor:zones-loaded', onZonesLoaded);
    EventBus.on('editor:zone-drawn', onZoneDrawn);
    EventBus.on('editor:zone-selected', onZoneSelected);
    return () => {
      EventBus.off('editor:zones-loaded', onZonesLoaded);
      EventBus.off('editor:zone-drawn', onZoneDrawn);
      EventBus.off('editor:zone-selected', onZoneSelected);
    };
  }, []);

  // ── Glisser-déposer une zone existante — prévisualisation uniquement ──
  // Rien n'est écrit en DB ici : la modal s'ouvre avec la nouvelle position,
  // handleZoneSave() l'envoie seulement si l'utilisateur valide ("Modifier").
  useEffect(() => {
    const onZoneMovePreview = (data: MapZone & { revertX: number; revertY: number }) => {
      setZoneModalError(null);
      setZoneModal({ ...data, type: data.type as ZoneType, id: data.id });
      setZoneRevertPos({ id: data.id, x: data.revertX, y: data.revertY });
    };
    EventBus.on('editor:zone-move-preview', onZoneMovePreview);
    return () => { EventBus.off('editor:zone-move-preview', onZoneMovePreview); };
  }, []);

  useEffect(() => {
    const onPlacedSelected = (data: { id: string; furnitureId: string } | null) => setPlacedSelection(data);
    EventBus.on('editor:placed-furniture-selected', onPlacedSelected);
    return () => { EventBus.off('editor:placed-furniture-selected', onPlacedSelected); };
  }, []);

  useEffect(() => {
    const onToast = ({ message }: { message: string }) => setEditorToast(message);
    EventBus.on('editor:toast', onToast);
    return () => { EventBus.off('editor:toast', onToast); };
  }, []);

  useEffect(() => {
    if (!editorToast) return;
    const t = setTimeout(() => setEditorToast(null), 4000);
    return () => clearTimeout(t);
  }, [editorToast]);

  // ── Dirty tracking (ME-4a) ────────────────────────────────
  useEffect(() => {
    const markDirty = () => setIsDirty(true);
    EventBus.on('editor:furniture-placed', markDirty);
    EventBus.on('editor:furniture-removed', markDirty);
    EventBus.on('editor:zone-created', markDirty);
    EventBus.on('editor:zone-updated', markDirty);
    EventBus.on('editor:zone-deleted', markDirty);
    EventBus.on('editor:wall-placed', markDirty);
    EventBus.on('editor:wall-removed', markDirty);
    EventBus.on('editor:floor-painted', markDirty);
    return () => {
      EventBus.off('editor:floor-painted', markDirty);
      EventBus.off('editor:furniture-placed', markDirty);
      EventBus.off('editor:furniture-removed', markDirty);
      EventBus.off('editor:zone-created', markDirty);
      EventBus.off('editor:zone-updated', markDirty);
      EventBus.off('editor:zone-deleted', markDirty);
      EventBus.off('editor:wall-placed', markDirty);
      EventBus.off('editor:wall-removed', markDirty);
    };
  }, []);

  // ── Cleanup ────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, []);

  // ── Preview mode (ME-4c) ──────────────────────────────────
  const enterPreview = () => {
    modeBeforePreviewRef.current = currentMode;
    setIsPreview(true);
    setCurrentMode('select');
    EventBus.emit('editor:set-mode', { mode: 'select' });
    EventBus.emit('editor:toggle-grid', { visible: false });
    setSelectedFurnitureId(null);
    setSelectedZoneType(null);
    EventBus.emit('editor:select-zone-type', null as any);
    setSelectedWallStyleId(null);
    EventBus.emit('editor:select-wall-style', null);
    setSelectedDoorId(null);
    EventBus.emit('editor:select-door', null);
  };

  const exitPreview = () => {
    setIsPreview(false);
    const prev = modeBeforePreviewRef.current;
    setCurrentMode(prev);
    EventBus.emit('editor:set-mode', { mode: prev });
    EventBus.emit('editor:toggle-grid', { visible: true });
  };

  // ── Mode change ────────────────────────────────────────────
  const changeMode = (mode: Mode) => {
    setCurrentMode(mode);
    if (mode === 'floor' || mode === 'wall' || mode === 'door') setStructureMode(mode);
    if (mode === 'zone' || mode === 'text' || mode === 'slots') setOrganisationMode(mode);
    EventBus.emit('editor:set-mode', { mode });
    if (mode !== 'furniture') {
      setSelectedFurnitureId(null);
    }
    if (mode !== 'zone') {
      setSelectedZoneType(null);
      EventBus.emit('editor:select-zone-type', null as any);
    }
    if (mode !== 'wall') {
      setSelectedWallStyleId(null);
      EventBus.emit('editor:select-wall-style', null);
    }
    if (mode !== 'door') {
      setSelectedDoorId(null);
      EventBus.emit('editor:select-door', null);
    }
    if (mode === 'floor') {
      // EditorScene oublie le matériau en quittant le mode : on le renvoie.
      EventBus.emit('editor:floor-settings', { layer: floorLayer, tool: floorTool, value: floorValue });
    }
  };

  // ── Floor painting settings ───────────────────────────────
  const updateFloorSettings = (next: { layer?: 'floor' | 'overlay'; tool?: FloorTool; value?: string | null }) => {
    const layer = next.layer ?? floorLayer;
    const tool = next.tool ?? floorTool;
    // Changer de couche : le matériau choisi n'existe pas sur l'autre couche.
    const value = next.value !== undefined ? next.value : next.layer && next.layer !== floorLayer ? null : floorValue;
    setFloorLayer(layer);
    setFloorTool(tool);
    setFloorValue(value);
    EventBus.emit('editor:floor-settings', { layer, tool, value });
  };

  // ── Door selection ────────────────────────────────────────
  const selectDoor = (id: string | null) => {
    setSelectedDoorId(id);
    EventBus.emit('editor:select-door', id ? { id } : null);
  };

  // ── Wall style selection ──────────────────────────────────
  const selectWallStyle = (entry: WallCatalogEntry | null) => {
    setSelectedWallStyleId(entry?.id ?? null);
    EventBus.emit('editor:select-wall-style', entry ? { id: entry.id } : null);
  };

  // ── Zone type selection ──────────────────────────────────
  const selectZoneType = (type: ZoneType | null) => {
    setSelectedZoneType(type);
    EventBus.emit('editor:select-zone-type', type ? { type } : null as any);
  };

  // ── Zone CRUD handlers ───────────────────────────────────
  const handleZoneSave = async (data: { name: string; properties: Record<string, unknown> }) => {
    if (!jwt || !selectedSlug || !zoneModal) return;
    const headers = { ...authHeaders, 'Content-Type': 'application/json' };
    const mapId = selectedSlug;
    setZoneModalError(null);

    try {
      if (zoneModal.id) {
        // PATCH existing zone — inclut x/y/width/height à chaque fois
        // (pas seulement pour un glisser) : si zoneModal reflète une
        // prévisualisation de déplacement (editor:zone-move-preview), c'est
        // cette géométrie qui doit être validée ici, pas l'ancienne.
        const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/zones/${encodeURIComponent(zoneModal.id)}`, {
          method: 'PATCH', headers,
          body: JSON.stringify({
            name: data.name,
            properties: data.properties,
            x: zoneModal.x,
            y: zoneModal.y,
            width: zoneModal.width,
            height: zoneModal.height,
          }),
        });
        if (!res.ok) { setZoneModalError(await zoneErrorMessage(res)); return; }
        const updated = await res.json() as MapZone & { deskSlots?: SlotSummary };
        setMapZones((prev) => prev.map((z) => z.id === updated.id ? updated : z));
        EventBus.emit('editor:zone-updated', updated);
        setZoneRevertPos(null);
        if (updated.deskSlots) setEditorToast(slotSummary(updated.deskSlots));
        if (updated.type === 'desk-area') setDesksVersion((v) => v + 1);
      } else {
        // POST new zone
        const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/zones`, {
          method: 'POST', headers,
          body: JSON.stringify({
            type: zoneModal.type,
            name: data.name,
            properties: data.properties,
            x: zoneModal.x,
            y: zoneModal.y,
            width: zoneModal.width,
            height: zoneModal.height,
          }),
        });
        if (!res.ok) { setZoneModalError(await zoneErrorMessage(res)); return; }
        const created = await res.json() as MapZone & { deskSlots?: SlotSummary };
        setMapZones((prev) => [...prev, created]);
        EventBus.emit('editor:zone-created', created);
        if (created.deskSlots) {
          setEditorToast(slotSummary(created.deskSlots));
          setDesksVersion((v) => v + 1);
        }
        if (writeZoneName && NAMEABLE_ON_FLOOR.has(zoneModal.type)) void writeNameOnFloor(created);
      }
    } catch (err) {
      console.error('[EditorPage] zone save failed:', err);
      setZoneModalError('Erreur réseau : la zone n\u2019a pas été enregistrée.');
      return;
    }
    setZoneModal(null);
  };

  // « Écrire le nom au sol » : devant la porte de la salle (sinon au centre),
  // taille moyenne, couleur choisie dans le panneau Zones.
  const writeNameOnFloor = async (zone: MapZone) => {
    const spot = zoneNameSpot(zone, [...mapZones, zone]);
    if (!selectedSlug) return;
    try {
      const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/labels`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: zone.name.slice(0, MAX_LABEL_LENGTH),
          size: 'medium',
          color: zoneNameColor,
          tileX: spot.tileX,
          tileY: spot.tileY,
        }),
      });
      if (!res.ok) { setEditorToast('La zone est créée, mais son nom n\u2019a pas pu être écrit au sol.'); return; }
      const saved = await res.json() as MapLabel;
      setLabels((prev) => [...prev, saved]);
    } catch {
      setEditorToast('La zone est créée, mais son nom n\u2019a pas pu être écrit au sol.');
    }
  };

  const handleZoneDelete = async () => {
    if (!jwt || !selectedSlug || !zoneModal?.id) return;
    const mapId = selectedSlug;
    setZoneModalError(null);

    try {
      const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(mapId)}/zones/${encodeURIComponent(zoneModal.id)}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) { setZoneModalError(await zoneErrorMessage(res)); return; }
      setMapZones((prev) => prev.filter((z) => z.id !== zoneModal.id));
      EventBus.emit('editor:zone-deleted', { id: zoneModal.id });
      if (zoneModal.type === 'desk-area') setDesksVersion((v) => v + 1);
      setZoneRevertPos(null);
    } catch (err) {
      console.error('[EditorPage] zone delete failed:', err);
      setZoneModalError('Erreur réseau : la zone n\u2019a pas été supprimée.');
      return;
    }
    setZoneModal(null);
  };

  // ── Doors (mode "Portes") ──────────────────────────────────
  // EditorScene émet le clic ; la zone porte est créée ici comme les autres
  // zones (mêmes erreurs API, même état mapZones), puis EditorScene découpe
  // le mur sous la porte ('editor:door-placed').
  useEffect(() => {
    const onDoorPlace = async ({ doorSprite, x, y, width }: { doorSprite: string; x: number; y: number; width: number }) => {
      if (!selectedSlug) return;

      // Salle liée : bulle/salle de réunion qui recouvre la porte (bulle
      // dessinée jusqu'aux murs), sinon juste sous la porte (mur du haut de
      // la salle) ou juste au-dessus de sa face (mur du bas — la face du mur
      // occupe la case y-1, la salle commence en y-2).
      const rooms = mapZones.filter((z) => z.type === 'bubble' || z.type === 'meeting-room');
      const roomAt = (cx: number, cy: number) =>
        rooms.find((z) => cx >= z.x && cx < z.x + z.width && cy >= z.y && cy < z.y + z.height);
      let linked: MapZone | undefined;
      for (let k = 0; k < width && !linked; k++) linked = roomAt(x + k, y) ?? roomAt(x + k, y + 1) ?? roomAt(x + k, y - 2);

      const doorNames = new Set(mapZones.filter((z) => z.type === 'door').map((z) => z.name));
      let n = doorNames.size + 1;
      while (doorNames.has(`Porte ${n}`)) n++;

      try {
        const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/zones`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'door',
            name: `Porte ${n}`,
            properties: { doorSprite, linkedBubble: linked?.name ?? '' },
            x, y, width, height: 1,
          }),
        });
        if (!res.ok) { setEditorToast(await zoneErrorMessage(res)); return; }
        const created = await res.json() as MapZone;
        setMapZones((prev) => [...prev, created]);
        EventBus.emit('editor:zone-created', created);
        EventBus.emit('editor:door-placed', { x, y, width });
        if (linked) {
          setEditorToast(`Porte liée à « ${linked.name} » (modifiable dans Organisation › Zones).`);
          // Nom de la salle encore au centre (posé avant la porte) : il passe devant la porte.
          const autoLabel = labelsRef.current.find((l) => l.text === linked.name && isAutoCenter(l, linked));
          if (autoLabel) {
            const spot = spotInFrontOfDoor(linked, created);
            const moved = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/labels/${encodeURIComponent(autoLabel.id)}`, {
              method: 'PATCH',
              headers: { ...authHeaders, 'Content-Type': 'application/json' },
              body: JSON.stringify(spot),
            }).catch(() => null);
            if (moved?.ok) {
              const updated = await moved.json() as MapLabel;
              setLabels((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
            }
          }
        }
      } catch (err) {
        console.error('[EditorPage] door place failed:', err);
        setEditorToast('Erreur réseau : la porte n\u2019a pas été posée.');
      }
    };

    const onDoorRemove = async ({ id }: { id: string }) => {
      if (!selectedSlug) return;
      try {
        const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/zones/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: authHeaders,
        });
        if (!res.ok) { setEditorToast(await zoneErrorMessage(res)); return; }
        setMapZones((prev) => prev.filter((z) => z.id !== id));
        EventBus.emit('editor:zone-deleted', { id });
      } catch (err) {
        console.error('[EditorPage] door remove failed:', err);
        setEditorToast('Erreur réseau : la porte n\u2019a pas été supprimée.');
      }
    };

    EventBus.on('editor:door-place', onDoorPlace);
    EventBus.on('editor:door-remove', onDoorRemove);
    return () => {
      EventBus.off('editor:door-place', onDoorPlace);
      EventBus.off('editor:door-remove', onDoorRemove);
    };
  }, [selectedSlug, mapZones, authHeaders]);

  // ── Bureaux personnels (lecture seule) ─────────────────────
  // Positions calculées par l'API depuis l'espace : rechargées quand un
  // espace bureaux est déplacé, redimensionné ou supprimé.
  useEffect(() => {
    if (!sceneReady || !selectedSlug) return;
    fetch(`${API_BASE}/world/desks/personal?mapId=${encodeURIComponent(selectedSlug)}`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: PersonalDesk[]) => setPersonalDesks(data))
      .catch((err) => console.warn('[EditorPage] load personal desks failed:', err));
    fetch(`${API_BASE}/world/desks/slots?mapId=${encodeURIComponent(selectedSlug)}`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: DeskSlot[]) => setDeskSlots(data))
      .catch((err) => console.warn('[EditorPage] load desk slots failed:', err));
  }, [sceneReady, selectedSlug, desksVersion]);

  useEffect(() => {
    if (sceneReady) EventBus.emit('editor:personal-desks', { desks: personalDesks });
  }, [personalDesks, sceneReady]);

  useEffect(() => {
    if (sceneReady) EventBus.emit('editor:desk-slots', { slots: deskSlots });
  }, [deskSlots, sceneReady]);

  // ── Emplacements de bureaux (Organisation › Emplacements) ──
  // EditorScene émet les gestes ; l'API valide (espace, chevauchement, obstacles).
  const slotRequest = async (method: 'POST' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<Response | null> => {
    try {
      return await fetch(`${API_BASE}/admin/desks/slots${path}`, {
        method,
        headers: { ...authHeaders, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      setEditorToast('Erreur réseau : l\u2019emplacement n\u2019a pas été modifié.');
      return null;
    }
  };

  useEffect(() => {
    const onAdd = async ({ areaZoneId, x, y }: { areaZoneId: string; x: number; y: number }) => {
      const res = await slotRequest('POST', '', { areaZoneId, x, y });
      if (!res) return;
      if (!res.ok) setEditorToast(await slotErrorMessage(res));
      setDesksVersion((v) => v + 1);
    };
    const onMove = async ({ id, x, y }: { id: string; x: number; y: number }) => {
      const res = await slotRequest('PATCH', `/${encodeURIComponent(id)}`, { x, y });
      if (!res) return;
      if (!res.ok) setEditorToast(await slotErrorMessage(res));
      setDesksVersion((v) => v + 1);
    };
    const onRemove = async ({ id }: { id: string }) => {
      const res = await slotRequest('DELETE', `/${encodeURIComponent(id)}`);
      if (!res) return;
      if (!res.ok) setEditorToast(await slotErrorMessage(res));
      setDesksVersion((v) => v + 1);
    };
    const onDeskMove = async ({ id, slotId, x, y }: { id: string; slotId?: string; x?: number; y?: number }) => {
      try {
        const res = await fetch(`${API_BASE}/admin/desks/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify(slotId ? { slotId } : { x, y }),
        });
        if (!res.ok) setEditorToast(await slotErrorMessage(res));
      } catch {
        setEditorToast('Erreur réseau : le bureau n\u2019a pas été déplacé.');
      }
      setDesksVersion((v) => v + 1);
    };
    EventBus.on('editor:desk-move', onDeskMove);
    EventBus.on('editor:slot-add', onAdd);
    EventBus.on('editor:slot-move', onMove);
    EventBus.on('editor:slot-remove', onRemove);
    return () => {
      EventBus.off('editor:desk-move', onDeskMove);
      EventBus.off('editor:slot-add', onAdd);
      EventBus.off('editor:slot-move', onMove);
      EventBus.off('editor:slot-remove', onRemove);
    };
  }, [authHeaders]);

  const regenerateSlots = async (areaZoneId: string) => {
    setSlotBusy(true);
    const res = await slotRequest('POST', '/generate', { areaZoneId });
    setSlotBusy(false);
    if (!res) return;
    if (!res.ok) { setEditorToast(await slotErrorMessage(res)); return; }
    setEditorToast(slotSummary(await res.json() as SlotSummary));
    setDesksVersion((v) => v + 1);
  };

  const placeDesksOnSlots = async (areaZoneId: string) => {
    setSlotBusy(true);
    const res = await slotRequest('POST', '/place-desks', { areaZoneId });
    setSlotBusy(false);
    if (!res) return;
    if (!res.ok) { setEditorToast(await slotErrorMessage(res)); return; }
    const { moved, unplaced } = await res.json() as { moved: number; unplaced: number };
    setEditorToast(
      `${moved} bureau${moved > 1 ? 'x' : ''} placé${moved > 1 ? 's' : ''} sur un emplacement`
      + (unplaced > 0 ? `, ${unplaced} sans emplacement libre (ajoute des emplacements puis recommence).` : '.'),
    );
    setDesksVersion((v) => v + 1);
  };

  // ── Text labels (mode "Texte") ────────────────────────────
  // La liste est tenue ici et renvoyée à EditorScene à chaque changement.
  useEffect(() => {
    if (!sceneReady || !selectedSlug) return;
    fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/labels`, { headers: authHeaders })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data: MapLabel[]) => setLabels(data))
      .catch((err) => console.warn('[EditorPage] load labels failed:', err));
  }, [sceneReady, selectedSlug]);

  useEffect(() => {
    if (sceneReady) EventBus.emit('editor:labels-updated', { labels });
  }, [labels, sceneReady]);

  useEffect(() => {
    const onClick = ({ tileX, tileY, id }: { tileX: number; tileY: number; id: string | null }) => {
      setLabelModalError(null);
      setLabelModal({ tileX, tileY, label: id ? labels.find((l) => l.id === id) ?? null : null });
    };
    const onMoved = async ({ id, tileX, tileY }: { id: string; tileX: number; tileY: number }) => {
      if (!selectedSlug) return;
      const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/labels/${encodeURIComponent(id)}`, {
        method: 'PATCH', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tileX, tileY }),
      }).catch(() => null);
      if (!res?.ok) {
        setEditorToast('Le texte n\'a pas pu être déplacé.');
        setLabels((prev) => [...prev]); // redessine à l'ancienne position
        return;
      }
      const updated = await res.json() as MapLabel;
      setLabels((prev) => prev.map((l) => l.id === id ? updated : l));
      setIsDirty(true);
    };
    EventBus.on('editor:label-click', onClick);
    EventBus.on('editor:label-moved', onMoved);
    return () => {
      EventBus.off('editor:label-click', onClick);
      EventBus.off('editor:label-moved', onMoved);
    };
  }, [labels, selectedSlug, authHeaders]);

  const saveLabel = async (data: { text: string; size: LabelSize; color: LabelColor }) => {
    if (!selectedSlug || !labelModal) return;
    const base = `${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/labels`;
    const existing = labelModal.label;
    try {
      const res = await fetch(existing ? `${base}/${encodeURIComponent(existing.id)}` : base, {
        method: existing ? 'PATCH' : 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(existing ? data : { ...data, tileX: labelModal.tileX, tileY: labelModal.tileY }),
      });
      if (!res.ok) { setLabelModalError(`Erreur serveur (HTTP ${res.status}).`); return; }
      const saved = await res.json() as MapLabel;
      setLabels((prev) => existing ? prev.map((l) => l.id === saved.id ? saved : l) : [...prev, saved]);
      setIsDirty(true);
      setLabelModal(null);
    } catch {
      setLabelModalError('Erreur réseau : le texte n\u2019a pas été enregistré.');
    }
  };

  const deleteLabel = async () => {
    if (!selectedSlug || !labelModal?.label) return;
    const id = labelModal.label.id;
    try {
      const res = await fetch(`${API_BASE}/admin/map/${encodeURIComponent(selectedSlug)}/labels/${encodeURIComponent(id)}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (!res.ok) { setLabelModalError(`Erreur serveur (HTTP ${res.status}).`); return; }
      setLabels((prev) => prev.filter((l) => l.id !== id));
      setIsDirty(true);
      setLabelModal(null);
    } catch {
      setLabelModalError('Erreur réseau : le texte n\u2019a pas été supprimé.');
    }
  };

  // ── Onglets et raccourcis ─────────────────────────────────
  const currentTab = tabOf(currentMode);
  const selectTab = (tab: Tab) => {
    changeMode(tab === 'structure' ? structureMode : tab === 'organisation' ? organisationMode : tab);
  };

  // 1–4 : onglets ; S/M/P : Sols, Murs, Portes ; Z/T/E : Zones, Textes, Emplacements.
  // Ignorés pendant une saisie, une fenêtre ouverte ou la prévisualisation.
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
    if (isPreview || zoneModal || labelModal || newMapOpen || deleteMapOpen || !sceneReady) return;
    // R (position physique, même touche en AZERTY) : vue suivante de l'objet,
    // Maj+R : précédente. Fantôme avant la pose, sinon meuble posé sélectionné.
    if (e.code === 'KeyR') {
      const step = e.shiftKey ? -1 : 1;
      if (currentMode === 'furniture' && selectedFurnitureId) {
        const next = nextOrientation(selectedFurnitureId, step);
        if (next !== selectedFurnitureId) { e.preventDefault(); selectFurniture(findFurniture(next) ?? null); }
      } else if (placedSelection) {
        e.preventDefault();
        EventBus.emit('editor:rotate-placed-furniture', { step });
      }
      return;
    }
    const k = e.key.toLowerCase();
    // Chiffres par position physique : sans Maj, un clavier AZERTY donne & é " '.
    const digit = /^(?:Digit|Numpad)([0-9])$/.exec(e.code)?.[1];
    const tab = TABS.find((x) => x.key === (digit ?? k));
    const mode: Mode | undefined = tab ? undefined
      : ({ s: 'floor', m: 'wall', p: 'door', z: 'zone', t: 'text', e: 'slots' } as Record<string, Mode>)[k];
    if (tab) { e.preventDefault(); selectTab(tab.id); }
    else if (mode) { e.preventDefault(); changeMode(mode); }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyHandlerRef.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Furniture selection ────────────────────────────────────
  const emitFurniture = (id: string, url: string, layer: string, isSolid: boolean, solidBaseOnly = furnitureSolidBaseOnly) => {
    EventBus.emit('editor:select-furniture', { id, url, layer, isSolid, solidBaseOnly });
  };

  const selectFurniture = (entry: FurnitureCatalogEntry | null) => {
    if (entry) {
      setSelectedFurnitureId(entry.id);
      // Pont, ponton : toujours « au sol » (sous les avatars, et c'est ce qui
      // le rend praticable sur l'eau — voir FurnitureCatalogEntry.walkable).
      const layer = entry.walkable || entry.floorLayer ? 'floor' : furnitureLayer;
      if (layer !== furnitureLayer) setFurnitureLayer(layer);
      emitFurniture(entry.id, entry.url, layer, furnitureIsSolid);
    } else {
      setSelectedFurnitureId(null);
      EventBus.emit('editor:select-furniture', null as any);
    }
  };

  const changeFurnitureLayer = (layer: string) => {
    setFurnitureLayer(layer);
    if (selectedFurnitureId) {
      const entry = findFurniture(selectedFurnitureId);
      if (entry) emitFurniture(entry.id, entry.url, layer, furnitureIsSolid);
    }
  };

  const changeFurnitureIsSolid = (solid: boolean) => {
    setFurnitureIsSolid(solid);
    if (selectedFurnitureId) {
      const entry = findFurniture(selectedFurnitureId);
      if (entry) emitFurniture(entry.id, entry.url, furnitureLayer, solid);
    }
  };

  const changeFurnitureSolidBaseOnly = (baseOnly: boolean) => {
    setFurnitureSolidBaseOnly(baseOnly);
    if (selectedFurnitureId) {
      const entry = findFurniture(selectedFurnitureId);
      if (entry) emitFurniture(entry.id, entry.url, furnitureLayer, furnitureIsSolid, baseOnly);
    }
  };

  const structureTabs = (
    <div className="pl-esub">
      <Tabs
        aria-label="Structure"
        items={[{ id: 'floor', label: 'Sols' }, { id: 'wall', label: 'Murs' }, { id: 'door', label: 'Portes' }]}
        value={structureMode}
        onChange={(m) => changeMode(m)}
      />
      <span className="pl-help">S · M · P</span>
    </div>
  );
  const organisationTabs = (
    <div className="pl-esub">
      <Tabs
        aria-label="Organisation"
        items={[{ id: 'zone', label: 'Zones' }, { id: 'text', label: 'Textes' }, { id: 'slots', label: 'Emplacements' }]}
        value={organisationMode}
        onChange={(m) => changeMode(m)}
      />
      <span className="pl-help">Z · T · E</span>
    </div>
  );

  return (
    <div className="pl-editor">
      <header className="pl-ebar">
        {isPreview ? (
          <>
            <Button variant="secondary" size="sm" icon="arrow-left" onClick={exitPreview}>Revenir à l'édition</Button>
            <span className="pl-help" style={{ marginLeft: 'auto' }}>Prévisualisation : la carte telle que les membres la verront.</span>
          </>
        ) : (
          <>
            <button type="button" className="pl-ebar-btn" aria-label="Retour à l'administration" title="Retour à l'administration" style={{ width: 34, padding: 0 }} onClick={() => useGameStore.getState().setAdminPage('members')}>
              <Icon name="arrow-left" size={16} />
            </button>
            <div style={{ width: 170 }}>
              <Select
                size="sm"
                aria-label="Carte à modifier"
                value={selectedSlug ?? ''}
                onChange={(e) => setSelectedSlug(e.target.value)}
                options={zones.map((z) => ({ value: z.slug, label: z.isDisabled ? `${z.displayName} (désactivée)` : z.displayName }))}
              />
            </div>
            {zones.find((z) => z.slug === selectedSlug)?.isArrival ? (
              <span title="On arrive sur cette carte en entrant dans le campus"><Badge tone="success">Carte d'arrivée</Badge></span>
            ) : zones.find((z) => z.slug === selectedSlug)?.isDisabled ? (
              <span title="Les membres et les invités ne peuvent pas y aller. Les admins, si."><Badge tone="warning">Désactivée</Badge></span>
            ) : selectedSlug && (
              <Button
                variant="secondary"
                size="sm"
                title="Faire arriver les membres et les invités sur cette carte en entrant dans le campus"
                onClick={() => {
                  const slug = selectedSlug;
                  fetch(`${API_BASE}/world/zones/${encodeURIComponent(slug)}/arrival`, { method: 'PUT', headers: authHeaders })
                    .then((r) => {
                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                      setZones((prev) => prev.map((z) => ({ ...z, isArrival: z.slug === slug })));
                    })
                    .catch((err) => console.warn('[EditorPage] carte d\'arrivée:', err));
                }}
              >
                Définir comme arrivée
              </Button>
            )}
            {selectedSlug && !zones.find((z) => z.slug === selectedSlug)?.isArrival && (
              <Button
                variant="ghost"
                size="sm"
                title={zones.find((z) => z.slug === selectedSlug)?.isDisabled
                  ? 'Rouvrir cette carte aux membres et aux invités'
                  : 'Fermer cette carte aux membres et aux invités sans la supprimer (les admins y ont toujours accès)'}
                onClick={() => { void toggleMapDisabled(selectedSlug); }}
              >
                {zones.find((z) => z.slug === selectedSlug)?.isDisabled ? 'Réactiver' : 'Désactiver'}
              </Button>
            )}
            <Button variant="primary" size="sm" icon="plus" onClick={() => setNewMapOpen(true)} title="Créer une carte vierge à construire dans l'éditeur">
              Nouvelle carte
            </Button>
            {isOwner && selectedSlug && (
              <Button
                variant="ghost"
                size="sm"
                disabled={!!zones.find((z) => z.slug === selectedSlug)?.isArrival}
                title={zones.find((z) => z.slug === selectedSlug)?.isArrival
                  ? 'La carte d’arrivée ne peut pas être supprimée : choisis d’abord une autre carte d’arrivée'
                  : 'Supprimer cette carte et tout ce qui est posé dessus'}
                onClick={() => setDeleteMapOpen(true)}
              >
                Supprimer la carte
              </Button>
            )}
            <span className="pl-ebar-sep" aria-hidden="true" />

            <nav aria-label="Outils" role="toolbar" className="pl-ebar-tools">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="pl-ebar-btn"
                  aria-pressed={currentTab === t.id}
                  aria-keyshortcuts={t.key}
                  onClick={() => selectTab(t.id)}
                >
                  <Icon name={t.icon} size={16} />{t.label}<kbd className="pl-ekey">{t.key}</kbd>
                </button>
              ))}
            </nav>

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div aria-label="Zoom" role="group" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <button type="button" className="pl-ebar-btn" aria-label="Dézoomer" style={{ width: 30, padding: 0 }} onClick={() => EventBus.emit('editor:zoom', { direction: 'out' })}><Icon name="minus" size={16} /></button>
                <button type="button" className="pl-ebar-btn" aria-label="Zoomer" style={{ width: 30, padding: 0 }} onClick={() => EventBus.emit('editor:zoom', { direction: 'in' })}><Icon name="plus" size={16} /></button>
              </div>
              <span className="pl-ebar-sep" aria-hidden="true" />
              <span className="pl-help" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {loading ? 'Chargement…' : isDirty ? <><Icon name="check" size={14} />Enregistré</> : 'Chaque modification est enregistrée'}
              </span>
              <Button variant="secondary" size="sm" icon="eye" onClick={enterPreview}>Prévisualiser</Button>
            </div>
          </>
        )}
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Canvas Phaser — panneau du mode à droite */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
          <main aria-label="Carte" ref={canvasRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }} />
          {!isPreview && placedSelection && (currentMode === 'select' || currentMode === 'furniture') && (() => {
            const entry = findFurniture(placedSelection.furnitureId);
            if (!entry) return null;
            return (
              <div className="pl-placed-bar" role="toolbar" aria-label="Objet sélectionné">
                <span className="pl-placed-bar-label">{entry.label}</span>
                {orientations(entry.id).length > 1
                  ? <ViewPicker id={entry.id} onPick={(furnitureId) => EventBus.emit('editor:rotate-placed-furniture', { furnitureId })} />
                  : <span className="pl-help">Une seule vue</span>}
              </div>
            );
          })()}
        </div>

        {!isPreview && currentMode === 'furniture' && (
          <FurniturePanel
            selectedId={selectedFurnitureId}
            onSelect={selectFurniture}
            layer={furnitureLayer}
            onLayerChange={changeFurnitureLayer}
            isSolid={furnitureIsSolid}
            onSolidChange={changeFurnitureIsSolid}
            solidBaseOnly={furnitureSolidBaseOnly}
            onSolidBaseOnlyChange={changeFurnitureSolidBaseOnly}
          />
        )}
        {!isPreview && currentMode === 'zone' && (
          <ZonePanel
            selectedType={selectedZoneType}
            onSelectType={selectZoneType}
            zones={mapZones}
            onSelectZone={(zone) => setZoneModal({ ...zone, type: zone.type as ZoneType, id: zone.id })}
            writeName={writeZoneName}
            onWriteNameChange={setWriteZoneName}
            nameColor={zoneNameColor}
            onNameColorChange={setZoneNameColor}
            sub={organisationTabs}
          />
        )}
        {!isPreview && currentMode === 'text' && <TextPanel count={labels.length} sub={organisationTabs} />}
        {!isPreview && currentMode === 'slots' && (
          <SlotPanel
            areas={mapZones.filter((z) => z.type === 'desk-area')}
            slots={deskSlots}
            deskCountByArea={personalDesks.reduce((m, d) => m.set(d.areaZoneId, (m.get(d.areaZoneId) ?? 0) + 1), new Map<string, number>())}
            freeDeskCountByArea={personalDesks.filter((d) => !d.slotId).reduce((m, d) => m.set(d.areaZoneId, (m.get(d.areaZoneId) ?? 0) + 1), new Map<string, number>())}
            onRegenerate={(id) => { void regenerateSlots(id); }}
            onPlaceDesks={(id) => { void placeDesksOnSlots(id); }}
            busy={slotBusy}
            sub={organisationTabs}
          />
        )}
        {!isPreview && currentMode === 'wall' && (
          <WallPanel selectedId={selectedWallStyleId} onSelect={selectWallStyle} sub={structureTabs} />
        )}
        {!isPreview && currentMode === 'floor' && (
          <FloorPanel
            layer={floorLayer}
            onLayerChange={(layer) => updateFloorSettings({ layer })}
            tool={floorTool}
            onToolChange={(tool) => updateFloorSettings({ tool })}
            value={floorValue}
            onValueChange={(value) => updateFloorSettings({ value })}
            sub={structureTabs}
          />
        )}
        {!isPreview && currentMode === 'door' && (
          <DoorPanel selectedId={selectedDoorId} onSelect={selectDoor} sub={structureTabs} />
        )}
      </div>

      {labelModal && (
        <LabelModal
          label={labelModal.label ?? { text: '', size: 'medium', color: 'white' }}
          onSave={saveLabel}
          onDelete={labelModal.label ? deleteLabel : undefined}
          onCancel={() => setLabelModal(null)}
          error={labelModalError}
        />
      )}

      {deleteMapOpen && selectedSlug && (
        <DeleteMapModal
          slug={selectedSlug}
          onCancel={() => setDeleteMapOpen(false)}
          onDeleted={() => {
            const deleted = selectedSlug;
            setDeleteMapOpen(false);
            const rest = zones.filter((z) => z.slug !== deleted);
            setZones(rest);
            setSelectedSlug((rest.find((z) => z.isArrival) ?? rest[0])?.slug ?? null);
            setEditorToast('Carte supprimée.');
          }}
        />
      )}

      {newMapOpen && (
        <NewMapModal
          existingSlugs={zones.map((z) => z.slug)}
          onCancel={() => setNewMapOpen(false)}
          onCreated={(slug, displayName) => {
            setNewMapOpen(false);
            setZones((prev) => [...prev, { slug, displayName }]);
            setSelectedSlug(slug);
          }}
        />
      )}

      {zoneModal && (
        <ZonePropertiesModal
          zone={zoneModal}
          onSave={handleZoneSave}
          onDelete={zoneModal.id ? handleZoneDelete : undefined}
          error={zoneModalError}
          availableZones={zones}
          bubbleNames={mapZones.filter((z) => z.type === 'bubble' || z.type === 'meeting-room').map((z) => z.name)}
          deskCount={zoneModal.id ? personalDesks.filter((d) => d.areaZoneId === zoneModal.id).length : 0}
          onCancel={() => {
            if (zoneRevertPos && zoneModal.id === zoneRevertPos.id) {
              EventBus.emit('editor:zone-move-rejected', { id: zoneRevertPos.id, x: zoneRevertPos.x, y: zoneRevertPos.y });
            }
            setZoneModal(null);
            setZoneModalError(null);
            setZoneRevertPos(null);
          }}
        />
      )}

      {/* Message de l'éditeur (déplacement refusé, porte liée, erreur réseau) */}
      {editorToast && (
        <div className="pl-editor-toast">
          <Toast tone={/erreur|impossible|pas pu|chevauche|refus/i.test(editorToast) ? 'danger' : 'info'} title={editorToast} onClose={() => setEditorToast(null)} />
        </div>
      )}
    </div>
  );
}
